import { supabase } from '@/integrations/supabase/client';
import { globalVFS } from '@/OS/utils/storage/VFS';
import { Message as AppMessage, Chat as AppChat, Project as AppProject } from '@/types';

const sanitizeName = (name: string) =>
  name
    .trim()
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '-')
    .replace(/\s+/g, ' ')
    .slice(0, 100);

const shortId = (id: string) => (id || '').substring(0, 8);

// Helper: dispatch a filesystem change event so FileExplorer can sync/refresh
function dispatchFSChanged(path: string) {
  try {
    window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path } }));
  } catch {
    // no-op in non-browser contexts
  }
}

// Username used by OS FileExplorer (prefer profile.username, fallback to email handle)
async function getUsername(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const user = data.session?.user;
  if (!user) return 'User';

  // Try to read profile for a custom username
  const { data: profile } = await supabase
    .from('profiles')
    .select('username,email')
    .eq('id', user.id)
    .maybeSingle();

  const candidate =
    profile?.username ||
    (profile?.email || user.email || 'User').split('@')[0] ||
    'User';

  return candidate.replace(/[^a-zA-Z0-9]/g, '') || 'User';
}

// Paths (Windows-style)
export async function getUserBasePath(): Promise<string> {
  const username = await getUsername();
  return `C:\\User\\${username}`;
}
export async function getDocumentsPath(): Promise<string> {
  return `${await getUserBasePath()}\\Documents`;
}

async function getChatsRootPath(): Promise<string> {
  return `${await getDocumentsPath()}\\Chats`;
}

async function getProjectsRootPath(): Promise<string> {
  return `${await getDocumentsPath()}\\Projects`;
}

// Ensure a Windows-style chain of directories exists (e.g., "C:\\User\\name\\Documents\\Chats\\Chat-1234")
async function ensureChain(path: string): Promise<void> {
  const isWindowsAbs = /^([A-Za-z]:)\\/.test(path);
  const driveRoot = isWindowsAbs ? path.match(/^([A-Za-z]:)\\/)! [0] : 'C:\\';
  const rest = isWindowsAbs ? path.replace(/^([A-Za-z]:)\\/, '') : path.replace(/^\\+/, '');

  const segments = rest.split('\\').filter(Boolean);
  let current = driveRoot;

  for (const seg of segments) {
    current = current.endsWith('\\') ? current + seg : current + '\\' + seg;
    // eslint-disable-next-line no-await-in-loop
    const exists = await globalVFS.exists(current);
    if (!exists) {
      // eslint-disable-next-line no-await-in-loop
      await globalVFS.createDirectory(current);
    }
  }
}

function convFileName() { return 'conversation.txt'; }
function metaFileName() { return 'meta.json'; }
function accessMarkerName() { return '.os-access.txt'; }

async function getChatFolderPath(chatId: string, chatTitle: string) {
  const safeChat = sanitizeName(chatTitle?.length ? chatTitle : 'Chat');

  // Default: place chat under Documents\Chats\<ChatName>
  let folder = `${await getChatsRootPath()}\\${safeChat}`;

  const { data: chatRow } = await supabase
    .from('chats')
    .select('project_id')
    .eq('id', chatId)
    .maybeSingle();

  if (chatRow?.project_id) {
    const { data: projectRow } = await supabase
      .from('projects')
      .select('name')
      .eq('id', chatRow.project_id)
      .maybeSingle();

    const safeProject = sanitizeName(projectRow?.name || 'Project');
    // Place chat under Documents\Projects\<ProjectName>\<ChatName>
    folder = `${await getProjectsRootPath()}\\${safeProject}\\${safeChat}`;
  }

  return folder;
}

async function getProjectFolderPath(projectId: string, projectName: string) {
  const safe = sanitizeName(projectName?.length ? projectName : 'Project');
  // Place project under Documents\Projects\<ProjectName>
  return `${await getProjectsRootPath()}\\${safe}`;
}

export async function enableChatVFSAccess(chat: Pick<AppChat, 'id' | 'title'>, messages?: AppMessage[]) {
  const folder = await getChatFolderPath(chat.id, chat.title || 'Chat');
  await ensureChain(folder);

  const meta = {
    type: 'chat',
    id: chat.id,
    title: chat.title,
    enabledAt: new Date().toISOString(),
  };
  await globalVFS.save(`${folder}\\${metaFileName()}`, JSON.stringify(meta, null, 2));

  const transcript =
    (messages && messages.length
      ? messages.map((m) => `${m.role === 'user' ? 'You' : 'AI'}: ${m.content}`).join('\n\n')
      : `# Conversation\n\n(Empty)\n`) + '\n';

  await globalVFS.save(`${folder}\\${convFileName()}`, transcript);
  await globalVFS.save(`${folder}\\${accessMarkerName()}`, `enabled=true\nat=${new Date().toISOString()}`);

  // NEW: if messages already contain attachments, mirror them now
  if (messages && messages.length > 0) {
    const allAtts = messages
      .flatMap(m => m.attachments || [])
      .map(att => ({
        name: att.name || null,
        type: att.type,
        content: att.content
      }));

    if (allAtts.length > 0) {
      await saveChatAttachmentsToVFS(chat.id, chat.title || 'Chat', allAtts);
    }
  }

  // Notify OS Explorer
  dispatchFSChanged(folder);
  return folder;
}

export async function disableChatVFSAccess(chat: Pick<AppChat, 'id' | 'title'>) {
  const folder = await getChatFolderPath(chat.id, chat.title || 'Chat');
  const markerPath = `${folder}\\${accessMarkerName()}`;
  const exists = await globalVFS.exists(markerPath);
  if (exists) {
    await globalVFS.delete(markerPath);
  }
  dispatchFSChanged(folder);
}

export async function appendChatMessageToVFS(chatId: string, chatTitle: string, message: AppMessage) {
  const folder = await getChatFolderPath(chatId, chatTitle || 'Chat');
  await ensureChain(folder);
  const convPath = `${folder}\\${convFileName()}`;
  const existing = await globalVFS.load(convPath);
  const line = `${message.role === 'user' ? 'You' : 'AI'}: ${message.content}\n\n`;
  await globalVFS.save(convPath, (existing || '') + line);

  // Notify OS Explorer
  dispatchFSChanged(folder);
}

export async function saveChatAttachmentsToVFS(
  chatId: string,
  chatTitle: string,
  attachments: { name?: string | null; type: string; content: string }[]
) {
  if (!attachments || attachments.length === 0) return;
  const folder = await getChatFolderPath(chatId, chatTitle || 'Chat');
  const attDir = `${folder}\\Attachments`;
  await ensureChain(attDir);

  for (const att of attachments) {
    const baseName = sanitizeName(att.name || `attachment-${Date.now()}`);
    const hasExt = /\.[a-z0-9]+$/i.test(baseName);
    const fileName = hasExt ? baseName : `${baseName}.txt`;
    await globalVFS.save(`${attDir}\\${fileName}`, att.content);
  }

  // NEW: Also mirror into a central Chats folder (C:\User\<username>\Documents\Chats\<ChatName>)
  const chatsRoot = await getChatsRootPath();
  await ensureChain(chatsRoot);
  const chatAggDir = `${chatsRoot}\\${sanitizeName(chatTitle || 'Chat')}`;
  await ensureChain(chatAggDir);

  for (const att of attachments) {
    const baseName = sanitizeName(att.name || `attachment-${Date.now()}`);
    const hasExt = /\.[a-z0-9]+$/i.test(baseName);
    const fileName = hasExt ? baseName : `${baseName}.txt`;
    await globalVFS.save(`${chatAggDir}\\${fileName}`, att.content);
  }

  // Notify OS Explorer
  dispatchFSChanged(attDir);
  dispatchFSChanged(chatAggDir);
}

export async function enableProjectVFSAccess(project: Pick<AppProject, 'id' | 'name' | 'description'>) {
  const folder = await getProjectFolderPath(project.id, project.name);
  await ensureChain(folder);

  const meta = {
    type: 'project',
    id: project.id,
    name: project.name,
    description: project.description,
    enabledAt: new Date().toISOString(),
  };
  await globalVFS.save(`${folder}\\${metaFileName()}`, JSON.stringify(meta, null, 2));

  const readme = `# ${project.name}\n\n${project.description || 'No description.'}\n`;
  await globalVFS.save(`${folder}\\README.txt`, readme);
  await globalVFS.save(`${folder}\\${accessMarkerName()}`, `enabled=true\nat=${new Date().toISOString()}`);

  dispatchFSChanged(folder);
  return folder;
}

export async function disableProjectVFSAccess(project: Pick<AppProject, 'id' | 'name'>) {
  const folder = await getProjectFolderPath(project.id, project.name);
  const markerPath = `${folder}\\${accessMarkerName()}`;
  const exists = await globalVFS.exists(markerPath);
  if (exists) {
    await globalVFS.delete(markerPath);
  }
  dispatchFSChanged(folder);
}

// NEW: Rename/move a chat's VFS folder when the chat title changes
export async function renameChatVFSFolder(chatId: string, oldTitle: string, newTitle: string) {
  const oldFolder = await getChatFolderPath(chatId, oldTitle || 'Chat');
  const newFolder = await getChatFolderPath(chatId, newTitle || 'Chat');

  if (oldFolder === newFolder) return newFolder;

  await ensureChain(newFolder);

  // Copy core files if they exist
  const coreFiles = [metaFileName(), convFileName(), accessMarkerName()];
  for (const name of coreFiles) {
    const oldPath = `${oldFolder}\\${name}`;
    if (await globalVFS.exists(oldPath)) {
      const content = await globalVFS.load(oldPath);
      await globalVFS.save(`${newFolder}\\${name}`, content || '');
      await globalVFS.delete(oldPath);
    }
  }

  // Copy attachments if present
  const oldAttDir = `${oldFolder}\\Attachments`;
  const newAttDir = `${newFolder}\\Attachments`;
  if (await globalVFS.exists(oldAttDir)) {
    await ensureChain(newAttDir);
    const items = await globalVFS.list(oldAttDir);
    for (const entry of items) {
      const oldFile = `${oldAttDir}\\${entry}`;
      if (await globalVFS.exists(oldFile)) {
        const data = await globalVFS.load(oldFile);
        await globalVFS.save(`${newAttDir}\\${entry}`, data || '');
        await globalVFS.delete(oldFile);
      }
    }
    try { await globalVFS.delete(oldAttDir); } catch {}
  }

  // Try removing old empty folder (best-effort)
  try { await globalVFS.delete(oldFolder); } catch {}

  dispatchFSChanged(oldFolder);
  dispatchFSChanged(newFolder);

  return newFolder;
}